import React, { useMemo, useState } from 'react';
import { DraftResponse } from '../../../shared/types';
import { DraftService } from '../services/DraftService';
import { NFL_TEAM_NAMES } from '../constants';
import './DraftView.css';
import { getOwnerColor } from '../utils/ownerColors';
import { getFantasyTeamName } from '../utils/teamNames';

interface DraftViewProps {
  draft: DraftResponse;
  onDraftChange: (draft: DraftResponse) => void;
  onRefresh: () => Promise<void>;
}

const DraftView: React.FC<DraftViewProps> = ({ draft, onDraftChange, onRefresh }) => {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const picksByNumber = useMemo(
    () => new Map(draft.picks.map(pick => [pick.pickNumber, pick])),
    [draft.picks]
  );
  const picksByOwner = useMemo(() => {
    const counts: Record<string, number> = {};
    draft.draftOrder.forEach(owner => { counts[owner] = 0; });
    draft.picks.forEach(pick => { counts[pick.owner] = (counts[pick.owner] || 0) + 1; });
    return counts;
  }, [draft.draftOrder, draft.picks]);

  const refreshAfterError = async (error: unknown) => {
    setActionError(error instanceof Error ? error.message : 'Draft changed. Please try again.');
    await onRefresh().catch(() => undefined);
  };

  const submitPick = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedTeam || draft.complete) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await DraftService.makePick(selectedTeam, draft.nextPickNumber);
      setSelectedTeam('');
      onDraftChange(updated);
    } catch (error) {
      await refreshAfterError(error);
    } finally {
      setBusy(false);
    }
  };

  const undoLatest = async () => {
    const latest = draft.picks[draft.picks.length - 1];
    if (!latest || !window.confirm(`Undo ${getFantasyTeamName(latest.owner)} selecting ${latest.team}?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      onDraftChange(await DraftService.undoLatestPick(latest.pickNumber));
    } catch (error) {
      await refreshAfterError(error);
    } finally {
      setBusy(false);
    }
  };

  const deadline = draft.deadline
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(new Date(draft.deadline))
    : '';

  return (
    <main className="draft-view">
      <section className="draft-hero card">
        <div>
          <p className="draft-kicker">{draft.season} draft room</p>
          <h2>{draft.complete ? 'Draft complete' : `Pick ${draft.nextPickNumber} of 32`}</h2>
          <p className="muted">
            {draft.complete
              ? 'All teams assigned. Draft remains visible until Week 1.'
              : <>
                  <span style={{ color: getOwnerColor(draft.currentOwner) }}>
                    {getFantasyTeamName(draft.currentOwner)}
                  </span>
                  {' is on the clock.'}
                </>}
          </p>
          {deadline && <p className="draft-deadline">Locks {deadline}</p>}
        </div>

        {!draft.complete && (
          <form className="draft-pick-form" onSubmit={submitPick}>
            <label htmlFor="draft-team">NFL team</label>
            <div className="draft-pick-controls">
              <select
                id="draft-team"
                value={selectedTeam}
                onChange={event => setSelectedTeam(event.target.value)}
                disabled={busy}
              >
                <option value="">Choose a team</option>
                {draft.availableTeams.map(team => (
                  <option key={team.abbreviation} value={team.abbreviation}>
                    {team.name} ({team.abbreviation})
                  </option>
                ))}
              </select>
              <button className="draft-submit" type="submit" disabled={!selectedTeam || busy}>
                {busy ? 'Saving…' : 'Make pick'}
              </button>
            </div>
          </form>
        )}

        {actionError && <div className="draft-error" role="alert">{actionError}</div>}
      </section>

      <section className="draft-owner-summary" aria-label="Owner pick totals">
        {draft.draftOrder.map((owner, index) => (
          <div className="draft-owner-card card" key={owner}>
            <span className="draft-order-number">#{index + 1}</span>
            <strong style={{ color: getOwnerColor(owner) }}>{getFantasyTeamName(owner)}</strong>
            <span>{picksByOwner[owner] || 0} / 8 teams</span>
          </div>
        ))}
      </section>

      <section className="draft-board card" aria-label="Draft board">
        <div className="draft-board-heading">
          <div>
            <p className="draft-kicker">Snake order</p>
            <h2>Draft board</h2>
          </div>
          <button className="draft-undo" type="button" onClick={undoLatest} disabled={!draft.canUndo || busy}>
            Undo last pick
          </button>
        </div>

        {Array.from({ length: 8 }, (_, roundIndex) => {
          const round = roundIndex + 1;
          return (
            <div className="draft-round" key={round}>
              <h3>Round {round} <span aria-hidden="true">{round % 2 === 1 ? '→' : '←'}</span></h3>
              <div className={`draft-round-picks ${round % 2 === 0 ? 'reverse' : ''}`}>
                {Array.from({ length: 4 }, (_, position) => {
                  const pickNumber = roundIndex * 4 + (round % 2 === 1 ? position + 1 : 4 - position);
                  const pick = picksByNumber.get(pickNumber);
                  const owner = draft.draftOrder[position];
                  const isCurrent = pickNumber === draft.nextPickNumber && !draft.complete;
                  return (
                    <div className={`draft-slot ${isCurrent ? 'current' : ''}`} key={pickNumber}>
                      <span className="draft-slot-number">Pick {pickNumber}</span>
                      <strong style={{ color: getOwnerColor(owner) }}>{getFantasyTeamName(owner)}</strong>
                      <span className={pick ? 'draft-slot-team' : 'draft-slot-empty'}>
                        {pick ? `${NFL_TEAM_NAMES[pick.team]} (${pick.team})` : 'Available'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>
    </main>
  );
};

export default DraftView;
